// src/ui/loading-overlay.ts

export interface LoadingOverlay {
  el: HTMLElement;
  show(): void;
  hide(): void;
  setProgress(ratio: number, message: string): void;
  clearProgress(): void;
}

export function createLoadingOverlay(opts: { delayMs: number }): LoadingOverlay {
  const el = document.createElement('div');
  Object.assign(el.style, {
    position: 'absolute', inset: '0',
    background: 'rgba(0,0,0,0.59)',
    color: '#fff', display: 'none',
    alignItems: 'center', justifyContent: 'center',
    zIndex: '10', pointerEvents: 'none',
    fontSize: '14px',
  });

  // Default plain "Processing…" content. setProgress replaces this; clearProgress
  // restores it.
  const resetToPlain = () => {
    el.textContent = '';
    const t = document.createElement('div');
    t.textContent = 'Processing…';
    el.append(t);
  };
  resetToPlain();

  let timer: number | null = null;

  const show = () => {
    // Reset to plain text so a previous progress UI doesn't leak in.
    resetToPlain();
    if (timer != null) return;
    timer = window.setTimeout(() => { el.style.display = 'flex'; }, opts.delayMs);
  };
  const hide = () => {
    if (timer != null) { clearTimeout(timer); timer = null; }
    el.style.display = 'none';
  };

  const setProgress = (ratio: number, message: string) => {
    // Cancel any pending delayed-show; we want the progress UI visible now.
    if (timer != null) { clearTimeout(timer); timer = null; }
    el.style.display = 'flex';
    el.textContent = '';

    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; align-items: center; gap: 4px;';

    const text = document.createElement('div');
    text.textContent = message;

    const bar = document.createElement('div');
    bar.style.cssText = 'width: 80%; max-width: 320px; height: 6px; background: rgba(255,255,255,0.2); border-radius: 3px; overflow: hidden; margin-top: 8px;';
    const fill = document.createElement('div');
    const clamped = Math.max(0, Math.min(1, ratio));
    fill.style.cssText = `height: 100%; width: ${(clamped * 100).toFixed(1)}%; background: var(--accent); transition: width 0.1s linear;`;
    bar.append(fill);

    container.append(text, bar);
    el.append(container);
  };

  const clearProgress = () => {
    resetToPlain();
  };

  return { el, show, hide, setProgress, clearProgress };
}
